/**
 * Noibu Feature Flag — home page experiment ("home-page").
 *
 * NJS downloads and initializes the Noibu Feature Flag SDK from the CDN.
 * Do not install, import, or configure another feature flag SDK. There is no
 * npm dependency and no bundler step here — `window.NoibuFeatureFlag` is put on
 * the page by NJS, which is already live on carsncards.com.
 *
 * Both experiences are rendered server-side by Liquid. This script does not
 * build DOM; it decides which of the two already-rendered trees is visible by
 * setting a class on <html>. That keeps the control experience byte-for-byte
 * identical to today and means a flag failure is a no-op rather than a blank
 * home page.
 *
 * Variations:
 *   current-experience  -> html.nff-home--a  (control, the default)
 *   new-home-page       -> html.nff-home--b
 *
 * HOW THE CLIENT ARRIVES — AND HOW IT CAN FAIL TO
 * Noibu's Shopify ScriptTag injects collect-core.js on window.load. collect-core
 * then injects collect-ff.js — the bundle that defines window.NoibuFeatureFlag —
 * but only when NOIBUJS_CONFIG.feature_flag_key is set:
 *
 *   Ie().feature_flag_key && pn(new URL("https://cdn.noibu.com/collect-ff.js"))
 *
 * and collect-ff itself returns early without that key, so it never assigns
 * self.NoibuFeatureFlag. An unprovisioned store therefore gets no client and no
 * event saying so — the SDK is simply never heard from.
 *
 * That is what happened between 2026-08-21 and 2026-08-26: carsncards' config is
 * {"scriptID":"1.186.0","domain":"."} with no feature_flag_key, so no visitor was
 * ever bucketed and the experiment recorded zero exposures. Because control is
 * what the server already renders, the page looked correct the whole time.
 *
 * The fix for that is provisioning feature_flag_key, which cannot be done from
 * the theme. What this file can do is refuse to fail silently again: every
 * terminal state is now reported, so "flags never arrived" is distinguishable
 * from "visitor was bucketed to control". See reportState().
 */
(function () {
  'use strict';

  var FLAG = 'home-page';
  var CONTROL = 'current-experience';
  var VARIATION_B = 'new-home-page';
  var CACHE_KEY = 'nff.home-page';

  // Reported to Noibu as a custom attribute, and mirrored onto <html>.
  var REPORT_KEY = 'nff_home-page';
  var STATE_ATTR = 'data-nff-state';

  // The client provably cannot arrive before window.load (see the header), so
  // the countdown is anchored to the load event rather than to script execution.
  // Anchoring it to parse time would misreport a slow-loading page as broken.
  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-home--a';
  CLASSES[VARIATION_B] = 'nff-home--b';

  var root = document.documentElement;

  // ---------------------------------------------------------------------------
  // Diagnostics.
  //
  // A flag that never resolves used to be indistinguishable from a visitor
  // legitimately bucketed to control, because control is what the server
  // already renders. Every terminal state now lands in three places: an
  // attribute on <html> for QA, a console warning for devtools, and a Noibu
  // custom attribute so the state is segmentable in Explorations without
  // anyone opening devtools.
  //
  // NOIBUJS.addCustomAttribute is exposed by collect-core, which loads
  // independently of collect-ff — so this reporting works in exactly the case
  // where the flags do not. It is also the backstop for the flag's own
  // "Traffic by variation" chart: group on this attribute to read the split.
  // ---------------------------------------------------------------------------

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }

    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // A single throw is not terminal — collect-core may still be booting.
        // Keep retrying rather than dropping the exposure, which would bias the
        // arm instead of merely breaking it.
      }
      // ~20s of polling, comfortably past window.load on a slow mobile page.
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  // ---------------------------------------------------------------------------
  // Flag plumbing.
  // ---------------------------------------------------------------------------

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return; // Already resolved or errored — no point arming a timer.
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load — is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    // This script is deferred, so it runs long before collect-ff and the
    // listener below is the path that actually fires. The direct check is kept
    // for the case where the bundle somehow lands first — and does not rely on
    // addHandler replaying PROVIDER_READY for an already-ready provider.
    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  // ---------------------------------------------------------------------------
  // Rendering — swap which server-rendered tree is visible.
  // ---------------------------------------------------------------------------

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (root.classList.contains(className)) return;

    root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
    root.classList.add(className);
    root.setAttribute('data-nff-home', variation);

    document.dispatchEvent(
      new CustomEvent('noibu:home-variation', { detail: { variation: variation } })
    );
  }

  function remember(variation) {
    // Cached only so a returning visitor — the repeat collector who is this
    // store's primary customer — does not see an A-to-B swap on every visit.
    // The SDK remains the source of truth; this is a paint-time hint that the
    // inline head script reads. Wrapped because storage throws in private mode
    // and in some embedded webviews.
    try {
      window.localStorage.setItem(CACHE_KEY, variation);
    } catch (error) {
      /* no-op */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_home=new-home-page. Lets this be reviewed on a preview
    // theme before the flag exists in the Noibu console.
    try {
      var value = new URLSearchParams(window.location.search).get('nff_home');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable — fall through to the flag. */
    }
    return null;
  }

  function renderNewHomePage() {
    activate(VARIATION_B);
    remember(VARIATION_B);
  }

  function renderCurrentExperience() {
    activate(CONTROL);
    remember(CONTROL);
  }

  function render(client) {
    var variation = client.getStringValue(FLAG, CONTROL);

    switch (variation) {
      case VARIATION_B:
        // Variation B — the search-first, six-section home page.
        renderNewHomePage();
        return VARIATION_B;
      case CONTROL:
        // Variation A — the current experience (the control group).
        // Falls through to the default deliberately.
      default:
        // This should always be the current experience. Visitors excluded by
        // targeting, and visitors whose flags never resolved, land here.
        renderCurrentExperience();
        return CONTROL;
    }
  }

  var forced = forcedVariation();
  if (forced) {
    // Deliberately does not call remember() and does not touch the SDK, so a
    // forced preview neither poisons the visitor's cache nor records a flag
    // exposure that would skew the experiment's own numbers. It is not reported
    // to Noibu either, for the same reason.
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));

      // Flags can be updated server-side mid-session. Re-read so a visitor who
      // gets re-bucketed does not sit on a stale experience.
      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      // Do NOT remember() here. Caching a value that came from a failure would
      // make the paint-time script replay a decision the SDK never made, and
      // would hide the failure on every subsequent page view.
      //
      // If the paint-time script already replayed a cached variation, leave it
      // alone: yanking it back to control would rearrange a page the visitor is
      // already reading, to no benefit. Control is already what is visible when
      // nothing was replayed, so activate() here is just making that explicit.
      if (!root.hasAttribute('data-nff-home')) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
